import { useState } from 'react';
import type {
  LeadSortField,
  ListLeadsQuery,
  Tier,
} from '@auctionradar/shared';
import { useLeads } from '../api/hooks';
import { LeadCard } from '../components/LeadCard';
import { Pagination } from '../components/Pagination';

const TIERS: Tier[] = ['A', 'B', 'C', 'D'];

const SORT_OPTIONS: Array<{ value: LeadSortField; label: string }> = [
  { value: 'opportunity_score', label: 'Score' },
  { value: 'discount_pct', label: 'Έκπτωση' },
  { value: 'created_at', label: 'Πρόσφατα' },
  { value: 'auction_date', label: 'Ημ. δημοπρασίας' },
  { value: 'starting_price', label: 'Τιμή εκκίνησης' },
];

const PAGE_SIZE = 30;

type IaFilter = 'all' | 'raw' | 'iauction';
const IA_OPTIONS: Array<{ value: IaFilter; label: string }> = [
  { value: 'all', label: 'Όλα' },
  { value: 'raw', label: 'Μόνο νέα' },
  { value: 'iauction', label: 'Μόνο iAuction' },
];

export function FeedPage() {
  // One tier at a time — defaults to A (the top opportunities).
  const [tier, setTier] = useState<Tier>('A');
  const [iaFilter, setIaFilter] = useState<IaFilter>('all');
  const [round, setRound] = useState<number | null>(null);
  const [sort, setSort] = useState<LeadSortField>('opportunity_score');
  const [page, setPage] = useState(1);

  const offset = (page - 1) * PAGE_SIZE;
  const query: ListLeadsQuery = {
    sort,
    direction: 'desc',
    limit: PAGE_SIZE,
    offset,
    tier: [tier],
    ...(iaFilter !== 'all' && { inIauction: iaFilter === 'iauction' }),
    ...(round != null && { round }),
  };

  const { data, isLoading, isError, error } = useLeads(query);

  const total = data?.total ?? 0;
  const items = data?.items ?? [];
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const selectTier = (t: Tier) => {
    setPage(1);
    setTier(t);
  };
  const selectIaFilter = (f: IaFilter) => {
    setPage(1);
    setIaFilter(f);
  };
  const selectRound = (r: number | null) => {
    setPage(1);
    setRound(r);
  };

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Feed</h1>
          <p className="text-sm text-slate-500">
            {isLoading ? '…' : `${total} leads · Tier ${tier}`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1">
            <span className="mr-1 text-xs uppercase tracking-wide text-slate-500">
              Tier
            </span>
            {TIERS.map((t) => {
              const active = tier === t;
              return (
                <button
                  key={t}
                  onClick={() => selectTier(t)}
                  aria-pressed={active}
                  className={`rounded px-2.5 py-1 text-xs font-bold tracking-wide transition ${
                    active
                      ? 'bg-slate-900 text-white'
                      : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                  }`}
                >
                  {t}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-1">
            <span className="mr-1 text-xs uppercase tracking-wide text-slate-500">
              iAuction
            </span>
            {IA_OPTIONS.map((o) => {
              const active = iaFilter === o.value;
              return (
                <button
                  key={o.value}
                  onClick={() => selectIaFilter(o.value)}
                  aria-pressed={active}
                  className={`rounded px-2.5 py-1 text-xs font-medium transition ${
                    active
                      ? 'bg-slate-900 text-white'
                      : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                  }`}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-500">
            <span className="uppercase tracking-wide">Γύρος</span>
            <select
              value={round ?? ''}
              onChange={(e) =>
                selectRound(e.target.value === '' ? null : Number(e.target.value))
              }
              className="rounded border border-slate-200 bg-white px-2 py-1 text-sm text-slate-900"
            >
              <option value="">Όλοι</option>
              {[1, 2, 3, 4, 5].map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-xs text-slate-500">
            <span className="uppercase tracking-wide">Sort</span>
            <select
              value={sort}
              onChange={(e) => {
                setPage(1);
                setSort(e.target.value as LeadSortField);
              }}
              className="rounded border border-slate-200 bg-white px-2 py-1 text-sm text-slate-900"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {isError && (
        <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          Αποτυχία φόρτωσης: {String((error as Error).message)}
        </div>
      )}

      {isLoading && items.length === 0 ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="h-56 animate-pulse rounded-lg border border-slate-200 bg-white"
            />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-12 text-center text-sm text-slate-500">
          Κανένα lead στο Tier {tier}.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((lead) => (
            <LeadCard key={lead.id} lead={lead} />
          ))}
        </div>
      )}

      <Pagination page={page} pageCount={pageCount} onChange={setPage} />
    </div>
  );
}
